import QRCode from 'qrcode';
import { ArrowLeft, Check, Printer, Save } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { Logo } from '@/components/brand/Logo';
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  Field,
  Input,
  LoadingState,
  Select,
  useToast,
} from '@/components/ui';
import { useMe, useSession } from '@/features/auth/session-context';
import {
  useConsultationOptions,
  useEncounter,
  useEncounterActions,
  type ClinicalDocument,
  type Encounter,
} from '@/features/encounters/api';
import type { Prescription } from '@/features/orders/types';
import { isApiError } from '@/lib/api';
import { PatientIdentityText } from '@/pages/patients/PatientIdentityText';
import { useLivePrescription, verifyUrl } from './consult-utils';
import {
  latestReferral,
  referralDefaultReason,
  referralInputFromDocument,
  stringList,
  useReferralDraft,
  useSaveReferralDraft,
  type ReferralLetterInput,
  type ReferralUrgency,
} from './referral-utils';
import styles from './ReferralLetterPage.module.css';
import { CLINIC_TZ } from '@/lib/clinic-time';

function dateLabel(value: string | Date): string {
  return new Intl.DateTimeFormat('en-MY', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    timeZone: CLINIC_TZ,
  }).format(typeof value === 'string' ? new Date(value) : value);
}

function vitalsLabel(encounter: Encounter): string | null {
  const vitals = encounter.vitals ?? encounter.vitalsHistory[0];
  if (!vitals) return null;
  return (
    [
      vitals.bpSystolic !== null && vitals.bpDiastolic !== null
        ? `BP ${vitals.bpSystolic}/${vitals.bpDiastolic} mmHg`
        : null,
      vitals.pulse !== null ? `pulse ${vitals.pulse} bpm` : null,
      vitals.temperatureC !== null ? `${vitals.temperatureC} °C` : null,
      vitals.spo2 !== null ? `SpO₂ ${vitals.spo2}%` : null,
    ]
      .filter(Boolean)
      .join(' · ') || null
  );
}

function ReferralQr({ code }: { code: string }) {
  const { t } = useTranslation();
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    QRCode.toDataURL(verifyUrl(code), { margin: 0, width: 240 })
      .then((value) => live && setSrc(value))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [code]);
  return (
    <div className={styles.qr}>
      {src && <img src={src} alt={t('referral.qrAlt')} />}
      <span>{t('referral.scanToVerify')}</span>
    </div>
  );
}

function referralMedicines(
  document: ClinicalDocument | null,
  prescription: Prescription | null,
): string[] {
  const issued = document ? stringList(document.data.medicines) : [];
  if (issued.length) return issued;
  return prescription?.items.map((item) => item.medicine) ?? [];
}

function ReferralDocument({
  encounter,
  input,
  issued,
  prescription,
  qrImage,
}: {
  encounter: Encounter;
  input: ReferralLetterInput;
  issued: ClinicalDocument | null;
  prescription: Prescription | null;
  /** A supplied image keeps printing synchronous; undefined renders the live QR. */
  qrImage?: string | null;
}) {
  const { t } = useTranslation();
  const medicines = referralMedicines(issued, prescription);
  const allergies = issued
    ? stringList(issued.data.allergies)
    : encounter.patient.allergies.map((allergy) => allergy.substance);
  const vitals = issued
    ? typeof issued.data.vitals === 'string'
      ? issued.data.vitals
      : null
    : vitalsLabel(encounter);
  const mmc = issued
    ? typeof issued.data.doctorMmc === 'string'
      ? issued.data.doctorMmc
      : encounter.doctor.mmcNumber
    : encounter.doctor.mmcNumber;
  const issuedOn = issued ? issued.issuedAt : new Date();
  const urgency = t(`referral.urgency.${input.urgency}`);

  return (
    <article className={styles.document} data-testid="referral-document">
      <header className={styles.documentHead}>
        <Logo className={styles.documentLogo} aria-hidden="true" />
        <div>
          <strong>{encounter.branch.name}</strong>
          <span>{t('referral.clinic')}</span>
        </div>
      </header>

      <div className={styles.documentMeta}>
        <div>
          <span>{t('referral.date')}</span>
          <strong>{dateLabel(issuedOn)}</strong>
        </div>
        {issued && (
          <div>
            <span>{t('referral.reference')}</span>
            <strong className="mono">{issued.number}</strong>
          </div>
        )}
      </div>

      <div className={styles.documentTitle}>
        <span>{t('referral.documentKicker')}</span>
        <h2>{t('referral.documentTitle')}</h2>
      </div>

      <p>{t('referral.salutation')}</p>
      <p>
        {t('referral.reLine')} <strong>{encounter.patient.fullName}</strong>
        {encounter.patient.dateOfBirth && (
          <>
            {' · '}
            {t('referral.dob', {
              date: dateLabel(encounter.patient.dateOfBirth),
            })}
          </>
        )}
        <br />
        <PatientIdentityText identity={encounter.patient.identity} />
      </p>
      <p>
        {t('referral.introduction', {
          specialty: input.specialty || t('referral.specialistPlaceholder'),
          hospital: input.hospital || t('referral.hospitalPlaceholder'),
        })}
      </p>

      <section className={styles.letterSection}>
        <h3>{t('referral.urgencyLabel')}</h3>
        <p>{urgency}</p>
      </section>
      <section className={styles.letterSection}>
        <h3>{t('referral.reason')}</h3>
        <p className={styles.reasonPreview}>
          {input.reason || t('referral.reasonPlaceholder')}
        </p>
      </section>
      {(allergies.length > 0 || medicines.length > 0 || vitals) && (
        <section className={styles.clinicalSummary}>
          <h3>{t('referral.clinicalSummary')}</h3>
          {allergies.length > 0 && (
            <p>
              <strong>{t('referral.allergies')}:</strong> {allergies.join(', ')}
            </p>
          )}
          {medicines.length > 0 && (
            <p>
              <strong>{t('referral.medicines')}:</strong> {medicines.join(', ')}
            </p>
          )}
          {vitals && (
            <p>
              <strong>{t('referral.vitals')}:</strong> {vitals}
            </p>
          )}
        </section>
      )}

      <footer className={styles.signature}>
        <div>
          <span>{t('referral.signedBy')}</span>
          <strong>{encounter.doctor.fullName}</strong>
          <small>
            {mmc
              ? t('referral.mmc', { number: mmc })
              : t('referral.mmcNotRecorded')}
          </small>
        </div>
        {issued &&
          (qrImage === undefined ? (
            <ReferralQr code={issued.verificationCode} />
          ) : (
            <div className={styles.qr}>
              {qrImage && <img src={qrImage} alt={t('referral.qrAlt')} />}
              <span>{t('referral.scanToVerify')}</span>
            </div>
          ))}
      </footer>
    </article>
  );
}

function ReferralPrintSheet({
  encounter,
  input,
  issued,
  prescription,
  onDone,
}: {
  encounter: Encounter;
  input: ReferralLetterInput;
  issued: ClinicalDocument;
  prescription: Prescription | null;
  onDone: () => void;
}) {
  const [qr, setQr] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let live = true;
    QRCode.toDataURL(verifyUrl(issued.verificationCode), {
      margin: 0,
      width: 320,
    })
      .then((src) => {
        if (live) setQr(src);
      })
      .catch(() => undefined)
      .finally(() => {
        if (live) setReady(true);
      });
    return () => {
      live = false;
    };
  }, [issued.verificationCode]);

  useEffect(() => {
    if (!ready) return;
    window.addEventListener('afterprint', onDone, { once: true });
    window.print();
    return () => window.removeEventListener('afterprint', onDone);
  }, [onDone, ready]);

  return createPortal(
    <div className={styles.printRoot} data-testid="referral-print-sheet">
      <ReferralDocument
        encounter={encounter}
        input={input}
        issued={issued}
        prescription={prescription}
        qrImage={qr}
      />
    </div>,
    document.body,
  );
}

function canAuthorReferral(
  encounter: Encounter,
  meId: string,
  hasPermission: (p: string) => boolean,
) {
  return (
    hasPermission('encounters:write') &&
    encounter.status === 'open' &&
    encounter.doctor.id === meId &&
    !latestReferral(encounter)
  );
}

/** RL-1860: the draft lives on the server; the form starts from it. */
function ReferralEditor({ encounter }: { encounter: Encounter }) {
  const { t } = useTranslation();
  const me = useMe();
  const { hasPermission } = useSession();
  const canAuthor = canAuthorReferral(encounter, me.id, hasPermission);
  const draft = useReferralDraft(encounter.id, canAuthor);
  if (canAuthor && draft.isPending)
    return <LoadingState label={t('referral.loadingDraft')} />;
  if (canAuthor && draft.isError)
    return (
      <main className={styles.emptyMain} id="main" tabIndex={-1}>
        <ErrorState error={draft.error} onRetry={() => void draft.refetch()} />
      </main>
    );
  return (
    <ReferralForm
      encounter={encounter}
      saved={canAuthor ? (draft.data?.draft ?? null) : null}
    />
  );
}

function ReferralForm({
  encounter,
  saved,
}: {
  encounter: Encounter;
  saved: ReferralLetterInput | null;
}) {
  const { t } = useTranslation();
  const me = useMe();
  const { hasPermission } = useSession();
  const navigate = useNavigate();
  const toast = useToast();
  const actions = useEncounterActions(encounter.id);
  const { data: options } = useConsultationOptions();
  const prescription = useLivePrescription(encounter.id);
  const issued = latestReferral(encounter);
  const issuedInput = issued ? referralInputFromDocument(issued) : null;
  const canAuthor = canAuthorReferral(encounter, me.id, hasPermission);
  const draft = useReferralDraft(encounter.id, canAuthor);
  const saveMutation = useSaveReferralDraft(encounter.id);
  const [input, setInput] = useState<ReferralLetterInput>(
    () =>
      issuedInput ??
      saved ?? {
        specialty: '',
        hospital: '',
        urgency: 'routine',
        reason: referralDefaultReason(encounter),
      },
  );
  const [printing, setPrinting] = useState(false);
  const effectiveInput: ReferralLetterInput = {
    ...input,
    specialty: input.specialty || options?.referralSpecialties[0] || '',
  };
  // What the server holds (or the untouched form), so autosave only sends
  // real changes.
  const savedJson = useRef<string | null>(null);
  // Batch 8 review: once signing starts, a pending autosave must not bring
  // the draft back after the API removed it.
  const [issuing, setIssuing] = useState(false);

  // The raw form: the default specialty filled in from the options is not an edit.
  const inputJson = JSON.stringify(input);
  const { mutate: autosave } = saveMutation;
  useEffect(() => {
    if (!canAuthor || issuing) return;
    if (savedJson.current === null) {
      // Nothing saved yet: start saving once the doctor changes something.
      savedJson.current = inputJson;
      return;
    }
    if (inputJson === savedJson.current) return;
    const timer = window.setTimeout(() => {
      autosave(JSON.parse(inputJson) as ReferralLetterInput, {
        onSuccess: () => {
          savedJson.current = inputJson;
        },
      });
    }, 1500);
    return () => window.clearTimeout(timer);
  }, [autosave, canAuthor, inputJson, issuing]);

  const valid =
    effectiveInput.specialty.length > 0 &&
    effectiveInput.hospital.trim().length >= 2 &&
    effectiveInput.reason.trim().length >= 2;

  const failure = (error: unknown, title: string) => {
    toast.show({
      tone: 'error',
      title,
      body: isApiError(error) ? error.message : undefined,
      error,
    });
  };

  const saveDraft = async () => {
    try {
      await saveMutation.mutateAsync(effectiveInput);
      savedJson.current = JSON.stringify(effectiveInput);
      toast.show({
        tone: 'success',
        title: t('referral.draftSaved'),
        body: t('referral.draftSavedBody'),
      });
    } catch (error) {
      failure(error, t('referral.draftNotSaved'));
    }
  };

  const signAndIssue = async () => {
    setIssuing(true);
    try {
      await actions.submit.mutateAsync({
        path: 'referral-letters',
        body: {
          specialty: effectiveInput.specialty,
          hospital: effectiveInput.hospital.trim(),
          urgency: effectiveInput.urgency,
          reason: effectiveInput.reason.trim(),
        },
      });
      // The API removed the draft with the signed letter.
      savedJson.current = null;
      toast.show({ tone: 'success', title: t('referral.issued') });
    } catch (error) {
      setIssuing(false);
      failure(error, t('referral.notIssued'));
    }
  };

  if (!canAuthor && !issued)
    return (
      <main className={styles.emptyMain} id="main" tabIndex={-1}>
        <EmptyState
          title={t('referral.noneIssued')}
          body={t('referral.noneIssuedBody')}
          action={
            <Button
              onClick={() => void navigate(`/consultations/${encounter.id}`)}
            >
              {t('referral.back')}
            </Button>
          }
        />
      </main>
    );

  return (
    <>
      <header className={styles.topbar}>
        <div className={styles.topbarIdentity}>
          <Link
            to={`/consultations/${encounter.id}`}
            className={styles.backLink}
            aria-label={t('referral.back')}
          >
            <ArrowLeft size={18} />
          </Link>
          <Logo className={styles.logo} aria-hidden="true" />
          <div>
            <div className={styles.titleLine}>
              <h1>{t('referral.title')}</h1>
              <Badge tone={issued ? 'success' : 'warning'}>
                {issued
                  ? t('referral.statusIssued')
                  : t('referral.statusDraft')}
              </Badge>
            </div>
            <p>
              {encounter.patient.fullName} · {encounter.branch.name}
            </p>
          </div>
        </div>
        <div className={styles.actions}>
          {canAuthor && (
            <>
              {saveMutation.isError ? (
                <span className={styles.saveState} role="status">
                  {t('referral.autosaveFailed')}
                </span>
              ) : (
                draft.data?.updatedAt && (
                  <span className={styles.saveState} role="status">
                    {t('referral.autosaved', {
                      time: new Date(draft.data.updatedAt).toLocaleTimeString(
                        'en-GB',
                        {
                          hour: '2-digit',
                          minute: '2-digit',
                          timeZone: CLINIC_TZ,
                        },
                      ),
                    })}
                  </span>
                )
              )}
              <Button
                icon={<Save size={16} />}
                loading={saveMutation.isPending}
                onClick={() => void saveDraft()}
              >
                {t('referral.saveDraft')}
              </Button>
              <Button
                variant="primary"
                icon={<Check size={16} />}
                disabled={!valid}
                loading={actions.submit.isPending}
                onClick={() => void signAndIssue()}
              >
                {t('referral.signAndIssue')}
              </Button>
            </>
          )}
          {issued && (
            <Button
              variant="primary"
              icon={<Printer size={16} />}
              disabled={printing}
              onClick={() => setPrinting(true)}
            >
              {t('referral.print')}
            </Button>
          )}
        </div>
      </header>

      <main className={styles.page} id="main" tabIndex={-1}>
        <section className={styles.formPanel} aria-label={t('referral.form')}>
          <div className={styles.panelHeading}>
            <div>
              <p className="eyebrow">{t('referral.formKicker')}</p>
              <h2>{t('referral.details')}</h2>
            </div>
            {!canAuthor && <Badge>{t('referral.readOnly')}</Badge>}
          </div>
          <Field label={t('referral.referTo')}>
            <Select
              value={effectiveInput.specialty}
              disabled={!canAuthor}
              placeholder={t('referral.chooseSpecialty')}
              options={(
                options?.referralSpecialties ?? [effectiveInput.specialty]
              )
                .filter(Boolean)
                .map((specialty) => ({ value: specialty, label: specialty }))}
              onChange={(event) =>
                setInput((current) => ({
                  ...current,
                  specialty: event.target.value,
                }))
              }
            />
          </Field>
          <Field label={t('referral.hospital')}>
            <Input
              value={input.hospital}
              disabled={!canAuthor}
              placeholder={t('referral.hospitalExample')}
              onChange={(event) =>
                setInput((current) => ({
                  ...current,
                  hospital: event.target.value,
                }))
              }
            />
          </Field>
          <Field label={t('referral.urgencyLabel')}>
            <Select
              value={input.urgency}
              disabled={!canAuthor}
              options={(
                ['routine', 'within_2_weeks', 'urgent_today'] as const
              ).map((urgency) => ({
                value: urgency,
                label: t(`referral.urgency.${urgency}`),
              }))}
              onChange={(event) =>
                setInput((current) => ({
                  ...current,
                  urgency: event.target.value as ReferralUrgency,
                }))
              }
            />
          </Field>
          <Field
            label={t('referral.reason')}
            hint={canAuthor ? t('referral.reasonHint') : undefined}
          >
            <textarea
              className={styles.textarea}
              rows={9}
              value={input.reason}
              disabled={!canAuthor}
              placeholder={t('referral.reasonPlaceholder')}
              onChange={(event) =>
                setInput((current) => ({
                  ...current,
                  reason: event.target.value,
                }))
              }
            />
          </Field>
          <p className={styles.recordNote}>{t('referral.recordNote')}</p>
        </section>

        <section
          className={styles.previewPanel}
          aria-label={t('referral.preview')}
        >
          <div className={styles.previewHeading}>
            <div>
              <p className="eyebrow">{t('referral.livePreview')}</p>
              <h2>{t('referral.a4Preview')}</h2>
            </div>
            <span>{t('referral.previewHint')}</span>
          </div>
          <div className={styles.paper}>
            <ReferralDocument
              encounter={encounter}
              input={issuedInput ?? effectiveInput}
              issued={issued}
              prescription={prescription}
            />
          </div>
        </section>
      </main>

      {printing && issued && (
        <ReferralPrintSheet
          encounter={encounter}
          input={issuedInput ?? effectiveInput}
          issued={issued}
          prescription={prescription}
          onDone={() => setPrinting(false)}
        />
      )}
    </>
  );
}

/** RL-1062: full-screen referral authoring outside the sidebar shell. */
export function ReferralLetterPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const { data: encounter, isLoading, error, refetch } = useEncounter(id);

  if (isLoading)
    return (
      <div className={styles.statePage}>
        <LoadingState label={t('referral.loading')} />
      </div>
    );
  if (error)
    return (
      <div className={styles.statePage}>
        <ErrorState error={error} onRetry={() => void refetch()} />
      </div>
    );
  if (!encounter)
    return (
      <div className={styles.statePage}>
        <EmptyState title={t('referral.notFound')} />
      </div>
    );
  return <ReferralEditor encounter={encounter} />;
}
